/* ========================================================================
   VECTORPOINT — QUOTE BASKET
   assets/quote-basket.js

   A lightweight, client-side "second cart" for quote requests.
   Lives entirely in localStorage — Shopify has no native concept of a
   second basket, so this does not touch the real Shopify cart at all.

   Public API (window.QuoteBasket):
     .add(item)          add a product (or bump qty if already present)
     .remove(key)         remove a line by its key
     .setQty(key, qty)    update quantity for a line
     .getItems()          -> array of items
     .getCount()          -> total quantity across all lines
     .clear()             empty the basket
     .STORAGE_KEY          the localStorage key used, exposed for debugging

   Fires a `quote-basket:updated` CustomEvent on `document` any time the
   basket changes, with `detail.count` and `detail.items` — the header
   bubble (quote-icon-bubble.liquid) listens for this.
   ======================================================================== */

(function () {
  const STORAGE_KEY = 'vp_quote_basket';

  function readBasket() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (err) {
      console.error('QuoteBasket: failed to read storage', err);
      return [];
    }
  }

  function writeBasket(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (err) {
      console.error('QuoteBasket: failed to write storage', err);
    }
    broadcast(items);
  }

  function broadcast(items) {
    const count = items.reduce((sum, item) => sum + item.quantity, 0);
    document.dispatchEvent(
      new CustomEvent('quote-basket:updated', {
        detail: { count, items },
      })
    );
  }

  /**
   * item shape:
   * {
   *   key:            unique string — defaults to variantId || productId
   *   productId:      numeric Shopify product id
   *   variantId:      numeric Shopify variant id (optional)
   *   title:          product title
   *   variantTitle:   variant title, e.g. "UHF / Black" (optional)
   *   image:          image URL (optional)
   *   url:             product page URL
   *   quantity:       integer, defaults to 1
   * }
   */
  function add(item) {
    if (!item || (!item.productId && !item.key)) {
      console.error('QuoteBasket.add: item needs at least a productId or key');
      return;
    }

    const key = item.key || String(item.variantId || item.productId);
    const quantity = Math.max(1, parseInt(item.quantity, 10) || 1);

    const items = readBasket();
    const existing = items.find((line) => line.key === key);

    if (existing) {
      existing.quantity += quantity;
    } else {
      items.push({
        key,
        productId: item.productId || null,
        variantId: item.variantId || null,
        title: item.title || 'Product',
        variantTitle: item.variantTitle || '',
        image: item.image || '',
        url: item.url || '#',
        quantity,
      });
    }

    writeBasket(items);
  }

  function remove(key) {
    const items = readBasket().filter((line) => line.key !== key);
    writeBasket(items);
  }

  function setQty(key, qty) {
    const quantity = Math.max(1, parseInt(qty, 10) || 1);
    const items = readBasket();
    const line = items.find((l) => l.key === key);
    if (line) {
      line.quantity = quantity;
      writeBasket(items);
    }
  }

  function clear() {
    writeBasket([]);
  }

  function getItems() {
    return readBasket();
  }

  function getCount() {
    return readBasket().reduce((sum, item) => sum + item.quantity, 0);
  }

  window.QuoteBasket = {
    STORAGE_KEY,
    add,
    remove,
    setQty,
    clear,
    getItems,
    getCount,
  };

  // Fire once on initial page load so the header bubble has a correct
  // count immediately, without waiting for the first add/remove.
  document.addEventListener('DOMContentLoaded', function () {
    broadcast(readBasket());
  });
})();
